// Tagihan Vendor — form buat (#4b-3a, layar 3)
//
// Dua jalan masuk:
//   1. Dari detail PO  → /purchasing/invoices/new?po_id=<uuid>, PO terkunci
//   2. Berdiri sendiri → /purchasing/invoices/new, PO dipilih di sini
//
// NOL KONSTANTA BISNIS. Status PO yang boleh ditagih dibaca dari
// /meta/invoice.po_statuses_eligible; aturan nomor tagihan & jatuh tempo dari
// /meta/invoice.business_rules.
//
// YANG SENGAJA TIDAK DIHITUNG DI SINI: pratinjau "sisa pagu PO" (gerbang
// komitmen 027 — Σ tagihan aktif + Σ DP aktif ≤ nilai PO). /meta/invoice
// TIDAK mengumumkan status invoice mana yang dihitung aktif, dan menebaknya
// di JS berarti menyalin aturan akuntansi ke frontend lalu diam-diam
// menyimpang begitu backend berubah. Sampai meta menyediakannya, penolakan
// datang dari backend dengan pesan bisnisnya sendiri (db_errors memetakan
// check_violation ke 400 berikut pesan aslinya).
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { BusyButton } from "../../components/etams/BusyButton";
import { StatusBadge } from "../../components/etams/StatusBadge";
import { ArrowLeft, ReceiptText } from "lucide-react";
import Hint from "../../components/etams/Hint";
import { toast } from "sonner";
import { formatIDR, formatDate, todayISO } from "../../lib/format";
import { useStoreTimezones } from "../../lib/timezone";
import {
  createInvoice, getInvoiceMeta, getPO, apiErrorMessage,
} from "../../lib/purchasingApi";
import PemilihCari from "../../components/etams/PemilihCari";
import { cariPo, labelPo } from "../../lib/sumberPilihan";

// Label status PO — sama dengan PO_LIFECYCLE di backend (po_service.py), supaya kode mentah tidak tampil.
const LABEL_STATUS_PO = {
  PENDING_VENDOR: "Menunggu vendor", ISSUED: "Terbit", PARTIAL_RECEIVED: "Diterima sebagian",
  FULLY_RECEIVED: "Diterima penuh", COMPLETED: "Selesai", CLOSED: "Ditutup", CANCELLED: "Batal",
};
const labelStatusPo = (s) => LABEL_STATUS_PO[s] || "—";

export default function VendorInvoiceCreate() {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const poIdDariUrl = sp.get("po_id") || "";
  const { tzForRegion } = useStoreTimezones();

  const [meta, setMeta] = useState(null);
  //: PO terpilih (objek). Jalur ?po_id= membacanya langsung; jalur biasa lewat PemilihCari.
  const [po, setPo] = useState(null);
  const [loadingPO, setLoadingPO] = useState(!!poIdDariUrl);
  const [busy, setBusy] = useState(false);

  const [poId, setPoId] = useState(poIdDariUrl);
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [invoiceDate, setInvoiceDate] = useState(todayISO());
  const [dueDate, setDueDate] = useState("");
  const [totalAmount, setTotalAmount] = useState("");
  const [notes, setNotes] = useState("");
  // K197 butir 2: ongkir/biaya lain di dalam total, dicatat terpisah (tidak dibandingkan dengan nilai barang).
  const [biayaLain, setBiayaLain] = useState("");
  const [ketBiaya, setKetBiaya] = useState("");

  useEffect(() => {
    getInvoiceMeta()
      .then(r => setMeta(r.data))
      .catch(e => toast.error(apiErrorMessage(e, "Gagal memuat aturan tagihan.")));
  }, []);

  // Pemilih PO, bukan grid data. ⛔ KELAS H: dulu 200 PO TERBARU lalu disaring status —
  // lalu seluruh PO per status layak ditarik ke browser (ambilSemua). Sejak 28 Sep 2026
  // PO DICARI di server (PemilihCari, `status_in` = meta.po_statuses_eligible) — PO lama
  // yang masih layak tetap bisa ditemukan, tanpa memuat semuanya.
  const statusLayak = meta?.po_statuses_eligible || [];
  const cariPoLayak = (kata) => (statusLayak.length === 0
    ? Promise.resolve([]) : cariPo(kata, { status_in: statusLayak.join(",") }));

  // Jalan masuk utama tetap dari detail PO (?po_id=): PO-nya dibaca langsung, lalu
  // dinilai layak/tidak terhadap status dari meta (backend tetap menolak yang tidak layak).
  useEffect(() => {
    if (!meta || !poIdDariUrl) return;
    setLoadingPO(true);
    getPO(poIdDariUrl)
      .then(r => setPo((meta.po_statuses_eligible || []).includes(r.data?.status) ? r.data : null))
      .catch(e => {
        toast.error(apiErrorMessage(e, "Gagal memuat PO."));
        setPo(null);
      })
      .finally(() => setLoadingPO(false));
  }, [meta, poIdDariUrl]);
  const tz = tzForRegion(po?.region_id || po?.region?.id);
  const rules = meta?.business_rules || {};
  const perm = meta?.permissions || {};

  const nominal = Number(totalAmount);
  const nominalSah = Number.isFinite(nominal) && nominal > 0;
  const bisaKirim = !!poId && !!invoiceDate && nominalSah;

  const submit = async () => {
    if (!bisaKirim) {
      toast.error("PO, tanggal tagihan, dan nilai tagihan wajib diisi.");
      return;
    }
    setBusy(true);
    try {
      const r = await createInvoice({
        po_id: poId,
        invoice_number: invoiceNumber.trim() || null,
        invoice_date: invoiceDate,
        total_amount: nominal,
        due_date: dueDate || null,
        notes: notes.trim() || null,
        biaya_lain_idr: Number(biayaLain) || 0,
        biaya_lain_keterangan: ketBiaya.trim() || null,
      });
      toast.success("Tagihan dibuat — menunggu persetujuan Kepala Cabang/Owner.");
      navigate(`/purchasing/invoices/${r.data.id}`);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Gagal membuat tagihan."));
    } finally {
      setBusy(false);
    }
  };

  // Wewenang tetap ditegakkan backend + route guard; ini hanya supaya layar
  // tidak menampilkan form yang pengirimannya pasti ditolak.
  if (meta && !perm.create) {
    return (
      <div className="p-12 text-center text-muted-foreground text-sm" data-testid="inv-create-forbidden">
        Hanya Admin Purchasing yang membuat tagihan vendor. Owner sengaja tidak
        ikut membuat tagihan — pemisahan tugas, sama seperti Owner tidak
        mengajukan PR.
        <div className="mt-3">
          <Button variant="outline" size="sm" onClick={() => navigate("/purchasing/invoices")}>
            Kembali ke daftar tagihan
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl" data-testid="inv-create-page">
      <div className="flex items-start gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate("/purchasing/invoices")}
                data-testid="inv-create-back">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <ReceiptText className="h-6 w-6" /> Buat Tagihan Vendor
            <Hint>Tagihan masuk antrean persetujuan Kepala Cabang/Owner lebih dulu. Finance baru bisa membayar setelah disetujui.</Hint>
          </h1>
        </div>
      </div>

      <Card className="p-4 space-y-4">
        <div>
          <label className="text-sm font-medium">PO</label>
          {poIdDariUrl ? (
            <div className="mt-1 text-sm" data-testid="inv-create-po-locked">
              {po ? (
                <span className="flex items-center gap-2 flex-wrap">
                  <strong>{po.po_number}</strong>
                  <span className="text-muted-foreground">
                    · {po.vendor?.name || "vendor belum ditetapkan"}
                  </span>
                  {/* Status PO, bukan status tagihan — /meta/invoice tidak
                      mengenal kodenya, jadi tone netral dan teksnya apa
                      adanya. Label berwarna milik PO ada di layar PO. */}
                  <StatusBadge tone="neutral">{labelStatusPo(po.status)}</StatusBadge>
                </span>
              ) : loadingPO ? (
                <span className="text-muted-foreground">Memuat PO...</span>
              ) : (
                <span className="text-danger-soft-foreground">
                  PO ini tidak bisa ditagih. Hanya PO berstatus{" "}
                  {(meta?.po_statuses_eligible || []).map(labelStatusPo).join(", ")} yang boleh
                  menerima pengajuan pembayaran.
                </span>
              )}
            </div>
          ) : (
            <>
              <div className="mt-1">
                <PemilihCari value={po} onChange={(o) => { setPo(o || null); setPoId(o?.id || ""); }}
                             cari={cariPoLayak} label={labelPo}
                             keterangan={(o) => `${o?.vendor?.name || "tanpa vendor"} · ${formatIDR(o?.total_amount)}`}
                             tampilAwal placeholder="— Pilih PO —"
                             placeholderCari="Ketik nomor PO atau nama vendor…"
                             testId="inv-create-po-select" />
              </div>
              <p className="text-xs text-muted-foreground mt-1" data-testid="inv-create-no-po">
                Hanya PO berstatus {statusLayak.map(labelStatusPo).join(", ") || "yang layak"} yang
                bisa ditagih — PO harus sudah terbit (vendor dan harganya ditetapkan) lebih dulu.
              </p>
            </>
          )}
        </div>

        {po && (
          <div className="rounded border bg-muted/40 p-3 text-sm" data-testid="inv-create-po-info">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-2">
              <div>
                <span className="text-muted-foreground text-xs block">Vendor</span>
                {po.vendor?.name || "—"}
              </div>
              <div>
                <span className="text-muted-foreground text-xs block">Nilai PO</span>
                {formatIDR(po.total_amount)}
              </div>
              <div>
                <span className="text-muted-foreground text-xs block">Tanggal PO</span>
                {formatDate(po.po_date, tz)}
              </div>
              <div>
                <span className="text-muted-foreground text-xs block">Wilayah</span>
                {po.region?.name || "—"}
              </div>
            </div>
          </div>
        )}

        <div>
          <label className="text-sm font-medium">
            Nomor tagihan dari vendor{" "}
            {rules.invoice_number_optional && (
              <Hint>
                Opsional — vendor sering belum menerbitkan nomor saat pembayaran diajukan; sistem tetap memberi nomor sendiri
                {rules.internal_number_format ? ` (${rules.internal_number_format})` : ""}.
                {rules.invoice_number_unique_per_vendor && " Kalau diisi, nomornya harus unik untuk vendor itu."}
              </Hint>
            )}
          </label>
          <Input value={invoiceNumber} data-testid="inv-create-number"
                 placeholder="Boleh dikosongkan"
                 onChange={(e) => setInvoiceNumber(e.target.value)} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium">Tanggal tagihan</label>
            <Input type="date" value={invoiceDate} data-testid="inv-create-date"
                   onChange={(e) => setInvoiceDate(e.target.value)} />
          </div>
          <div>
            <label className="text-sm font-medium">Jatuh tempo</label>
            <Input type="date" value={dueDate} data-testid="inv-create-due"
                   onChange={(e) => setDueDate(e.target.value)} />
            {rules.due_date_null_means_cash && (
              <p className="text-xs text-muted-foreground mt-1">
                Kosongkan kalau tunai (bayar langsung). Terisi berarti termin.
              </p>
            )}
          </div>
        </div>

        <div>
          <label className="text-sm font-medium">Nilai tagihan (Rp)</label>
          <Input type="number" min="1" value={totalAmount} data-testid="inv-create-total"
                 onChange={(e) => setTotalAmount(e.target.value)} />
          <p className="text-xs text-muted-foreground mt-1">
            {po
              ? `Nilai PO ${po.po_number}: ${formatIDR(po.total_amount)}. Total seluruh tagihan aktif PO ini tidak boleh melebihi nilai PO.`
              : "Total seluruh tagihan aktif sebuah PO tidak boleh melebihi nilai PO itu."}
          </p>
          {totalAmount !== "" && !nominalSah && (
            <p className="text-xs text-danger-soft-foreground mt-1"
               data-testid="inv-create-total-error">
              Nilai tagihan harus lebih dari nol.
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <div>
            <label className="text-sm font-medium">Ongkir / biaya lain di dalam nilai tagihan (Rp)</label>
            <Input type="number" min="0" value={biayaLain} data-testid="inv-create-biaya-lain"
                   onChange={(e) => setBiayaLain(e.target.value)} />
          </div>
          <div>
            <label className="text-sm font-medium">Keterangan biaya lain</label>
            <Input value={ketBiaya} placeholder="mis. ongkir ekspedisi" data-testid="inv-create-biaya-lain-ket"
                   onChange={(e) => setKetBiaya(e.target.value)} />
          </div>
          <p className="text-xs text-muted-foreground sm:col-span-2">
            Biaya lain dicatat terpisah: tidak dihitung sebagai nilai barang, jadi tidak ikut dibandingkan dengan
            barang yang diterima. Tanpa biaya lain, kosongkan.
          </p>
        </div>

        <div>
          <label className="text-sm font-medium">Catatan</label>
          <Textarea value={notes} rows={2} data-testid="inv-create-notes"
                    onChange={(e) => setNotes(e.target.value)} />
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t">
          <Button variant="outline" onClick={() => navigate("/purchasing/invoices")}
                  disabled={busy}>
            Batal
          </Button>
          <BusyButton busy={busy} busyText="Mengirim..." disabled={!bisaKirim}
                      data-testid="inv-create-submit" onClick={submit}>
            Ajukan Tagihan
          </BusyButton>
        </div>
      </Card>
    </div>
  );
}
